import {
  BEHAVIORAL_HEALTH_ROLE,
  CARE_TEAM_ROLE_GROUPS,
  HEALTH_GUIDE_ROLE,
  MD_ROLE,
  NP_ROLE,
  RN_ROLE,
} from '~/shared/constants/careTeamRoles'
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'

import type { FC } from 'react'
import ProviderSelectionRadioButtonsGroup from './ProviderSelectionRadioButtonsGroup'
import { userCareTeamV2, type UserCareTeamV2ListResponse } from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'
import groupBy from 'lodash/fp/groupBy'
import { providerCanProvideClinicalCareToPatient } from '~/entities/provider'
import { patientsSlice } from '~/entities/person'
import { queueNotification } from '~/shared/notifications/actions'
import theme from '~/shared/lib/theme'
import type { RootState } from '~/app/store/reducers'
import type { PatientUser } from '~/shared/types'
import type { SelectedCareTeam } from './types'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

interface EditPatientCareTeamProps {
  patient: PatientUser
}

/** The assigned provider's id for `role`, or `null` when the role is unfilled. */
const findProviderInRole = (careTeam: UserCareTeamV2ListResponse, role: string) =>
  careTeam.find(provider => provider.group === role)?.id ?? null

/** Collapses the care-team list endpoint's rows into the editor's per-role selection. */
const careTeamToSelection = (careTeam: UserCareTeamV2ListResponse): SelectedCareTeam => ({
  md: findProviderInRole(careTeam, MD_ROLE),
  np: findProviderInRole(careTeam, NP_ROLE),
  rn: findProviderInRole(careTeam, RN_ROLE),
  healthGuide: findProviderInRole(careTeam, HEALTH_GUIDE_ROLE),
  behavioralHealth: findProviderInRole(careTeam, BEHAVIORAL_HEALTH_ROLE),
})

const EditPatientCareTeam: FC<EditPatientCareTeamProps> = props => {
  const allProviders = useSelector((state: RootState) => state.providers)
  const [behavioralHealthSwitch, setBehavioralHealthSwitch] = useState(false)
  const [showCancel, setShowCancel] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedCareTeam, setSelectedCareTeam] = useState<SelectedCareTeam>({
    md: null,
    np: null,
    rn: null,
    healthGuide: null,
    behavioralHealth: null,
  })
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const { patient } = props

  const groupedProviders = useMemo(() => {
    const providersWithStateInfo = allProviders
      .filter(provider => provider.is_active)
      .filter(provider => provider.provider_fields?.is_external === false)
      .map(provider => {
        return {
          displayName: `${provider.first_name} ${provider.last_name}`,
          canProvideCare: providerCanProvideClinicalCareToPatient(provider, patient),
          ...provider,
        }
      })
    return groupBy(provider => {
      const groupFound = provider.groups.find(g => CARE_TEAM_ROLE_GROUPS.has(g.name))
      return groupFound ? groupFound.name : 'unknown'
    }, providersWithStateInfo)
  }, [allProviders, patient])

  const behavioralHealthProviders = groupedProviders[BEHAVIORAL_HEALTH_ROLE] ?? []
  const soleBehavioralHealthProvider =
    behavioralHealthProviders.length === 1 ? behavioralHealthProviders[0] : undefined

  const reset = async () => {
    const { data: careTeam } = await userCareTeamV2.list({
      patient_id: String(patient.id),
    })
    setSelectedCareTeam(careTeamToSelection(careTeam))
    setShowCancel(false)
  }

  // A different patient shows the loader and hides Cancel until its care team arrives.
  const [loadingFor, setLoadingFor] = useState<number>()
  if (patient.id !== loadingFor) {
    setLoadingFor(patient.id)
    setLoading(true)
    setShowCancel(false)
  }

  useEffect(() => {
    // `ignore` drops the response of a patient this effect has already moved off.
    // Two loads can be in flight at once whenever `patient.id` changes while the
    // component stays mounted, and without this the slower one wins — assigning one
    // patient's care team to whoever is on screen, which `handleSave` would then
    // write back under the new patient's id.
    let ignore = false
    const fetchCareTeam = async () => {
      const { data: careTeam } = await userCareTeamV2.list({
        patient_id: String(patient.id),
      })
      if (ignore) {
        return
      }
      setSelectedCareTeam(careTeamToSelection(careTeam))
      setLoading(false)
    }
    fireAndForget('[CareTeam] load care team', fetchCareTeam)
    return () => {
      ignore = true
    }
  }, [patient.id])

  // The behavioral-health switch follows the selected care team whenever it changes.
  const [switchSetFrom, setSwitchSetFrom] = useState<typeof selectedCareTeam>()
  if (selectedCareTeam !== switchSetFrom) {
    setSwitchSetFrom(selectedCareTeam)
    setBehavioralHealthSwitch(!!selectedCareTeam.behavioralHealth)
  }

  const handleSave = async () => {
    try {
      await userCareTeamV2.partialUpdate(
        { patient_id: String(patient.id) },
        {
          data: {
            care_team: {
              md: selectedCareTeam.md,
              rn: selectedCareTeam.rn,
              health_guide: selectedCareTeam.healthGuide,
              behavioral_health: selectedCareTeam.behavioralHealth,
              np: selectedCareTeam.np,
            },
          },
        },
      )
      await queryClient.invalidateQueries({ queryKey: ['careTeam', patient.id] })
      setShowCancel(false)
      dispatch(
        queueNotification({
          variant: 'success',
          message: 'Care team updated',
        }),
      )
      fireAndForget('[Patient] refetch patient', () =>
        dispatch(patientsSlice.thunks.getPatient(patient.id)),
      )
    } catch (error) {
      dispatch(queueNotification({ variant: 'error', message: 'Problem updating care team' }))
      logger.error('[CareTeam] save care team', error)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Typography variant="h6">Care Team</Typography>
      </Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <ProviderSelectionRadioButtonsGroup
            roleName="md"
            selectedCareTeam={selectedCareTeam}
            title={'Physician'}
            options={groupedProviders[MD_ROLE] ?? []}
            setSelectedCareTeam={setSelectedCareTeam}
            setShowCancel={setShowCancel}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ProviderSelectionRadioButtonsGroup
            roleName="np"
            selectedCareTeam={selectedCareTeam}
            title={'Nurse Practitioner'}
            options={groupedProviders[NP_ROLE] ?? []}
            setSelectedCareTeam={setSelectedCareTeam}
            setShowCancel={setShowCancel}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ProviderSelectionRadioButtonsGroup
            roleName="rn"
            selectedCareTeam={selectedCareTeam}
            title={'Registered Nurse'}
            options={groupedProviders[RN_ROLE] ?? []}
            setSelectedCareTeam={setSelectedCareTeam}
            setShowCancel={setShowCancel}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ProviderSelectionRadioButtonsGroup
            roleName="healthGuide"
            selectedCareTeam={selectedCareTeam}
            title={'Health Guide'}
            options={groupedProviders[HEALTH_GUIDE_ROLE] ?? []}
            setSelectedCareTeam={setSelectedCareTeam}
            setShowCancel={setShowCancel}
            disabled={loading}
          />
        </Box>
      </Box>
      <Box sx={{ ml: -2, color: theme.palette.secondary.dark }}>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Switch
              color="primary"
              checked={behavioralHealthSwitch}
              onChange={() => {
                if (!behavioralHealthSwitch && soleBehavioralHealthProvider) {
                  setSelectedCareTeam({
                    ...selectedCareTeam,
                    behavioralHealth: soleBehavioralHealthProvider.id,
                  })
                } else if (behavioralHealthSwitch) {
                  setSelectedCareTeam({
                    ...selectedCareTeam,
                    behavioralHealth: null,
                  })
                }
                setShowCancel(true)
                setBehavioralHealthSwitch(!behavioralHealthSwitch)
              }}
            />
          }
          label="Behavioral Health"
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        {behavioralHealthSwitch ? (
          <ProviderSelectionRadioButtonsGroup
            roleName="behavioralHealth"
            selectedCareTeam={selectedCareTeam}
            options={behavioralHealthProviders}
            disabled={loading || soleBehavioralHealthProvider !== undefined}
            setSelectedCareTeam={setSelectedCareTeam}
            setShowCancel={setShowCancel}
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {showCancel ? (
          <Box sx={{ mr: 1 }}>
            <Button onClick={() => fireAndForget('[CareTeam] reset', reset)}>
              Cancel Care Team Changes
            </Button>
          </Box>
        ) : null}
        <Box>
          <Button
            type="submit"
            color="primary"
            variant="outlined"
            onClick={() => void handleSave()}
          >
            Save Care Team
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default EditPatientCareTeam
